import { useState, useEffect } from "react";
import { IconButton, Chip, Checkbox, Button } from "@material-ui/core";
import { FileCopy } from "@mui/icons-material";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import {
  columnCNPJCPFField,
  columnNumberField,
  columnPriceField,
  columnTextField,
} from "utils/tables-utils";
import BoletoDownloadIconButton from "components/_refactor/buttons/boleto-download-icon-button";
import checkSituationDays from "./check-situation-days";
import UtilsFncs from "utils/UtilsFncs";
import { isBoletoDownloadAvailable } from "utils/boleto-download";
import { LONGTERM_STATUS } from "../view/titulos-financeiros";

function sortableDateValue(cell) {
  if (cell == null || cell === "") return "";
  const s = String(cell).trim();
  if (s.length === 10 && s.includes("-")) return s.replace(/-/g, "");
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}${br[2]}${br[1]}`;
  return s;
}

export default function TableTitulosFull({
  data,
  totalRegistros,
  extraActions,
  onExportBoletos,
}) {
  const [dataTable, setDataTable] = useState([]);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    sizePerPage: 10,
  });

  const pageSize = paginationState.sizePerPage || 10;
  const currentPage = paginationState.page || 1;
  const pageStart = (currentPage - 1) * pageSize;
  const pageSlice = dataTable?.slice(pageStart, pageStart + pageSize) ?? [];
  const selectableOnPage = pageSlice.filter((item) => !item.disabled);
  const allSelectableOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((item) => item.selected);

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, [data]);

  useEffect(() => {
    setDataTable(
      data?.map((item, index) => {
        const selectKey =
          item.IdTitulo != null && String(item.IdTitulo).trim() !== ""
            ? String(item.IdTitulo)
            : item.Id != null && String(item.Id).trim() !== ""
              ? String(item.Id)
              : `__row_${index}`;
        return {
          ...item,
          disabled: !isBoletoDownloadAvailable(item),
          selected: false,
          __selectKey: selectKey,
        };
      }),
    );
  }, [data]);

  const handleSelectRow = (key) => {
    setDataTable((prevData) =>
      prevData?.map((item) => {
        if (item.__selectKey !== key || item.disabled) return item;
        return { ...item, selected: !item.selected };
      }),
    );
  };

  const handleSelectAllOnPage = () => {
    const size = paginationState.sizePerPage || 10;
    const p = paginationState.page || 1;
    const start = (p - 1) * size;
    setDataTable((prevData) => {
      const pageRows = prevData?.slice(start, start + size) ?? [];
      const pageKeys = new Set(pageRows.map((row) => row.__selectKey));
      const selectable = pageRows.filter((item) => !item.disabled);
      if (selectable.length === 0) return prevData;
      const allSelectedOnPage = selectable.every((item) => item.selected);
      const shouldSelectAll = !allSelectedOnPage;
      return prevData?.map((item) => {
        if (!pageKeys.has(item.__selectKey)) return item;
        return {
          ...item,
          selected: item.disabled ? false : shouldSelectAll,
        };
      });
    });
  };

  const columnModel = [
    {
      dataField: "selected",
      text: "",
      align: "center",
      headerAlign: "center",
      formatter: (_, row) => (
        <Checkbox
          checked={row.selected}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectRow(row.__selectKey);
          }}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          disabled={row.disabled}
        />
      ),
      headerFormatter: () => (
        <Checkbox
          checked={allSelectableOnPageSelected}
          onChange={handleSelectAllOnPage}
          color="primary"
          disabled={selectableOnPage.length === 0}
        />
      ),
      style: {
        width: "5vw",
      },
    },
    columnTextField({
      dataField: "boleto",
      text: "Boleto",
      align: "center",
      formatter: (_, row) => (
        <BoletoDownloadIconButton
          row={row}
          origem="lojista-360"
          icon="document"
        />
      ),
    }),
    columnNumberField({
      dataField: "NumeroTitulo",
      text: "Nr. Título",
    }),
    columnTextField({
      dataField: "Situacao",
      text: "Status",
      align: "center",
      formatter: (_, row) => {
        const status = row.Situacao === "LQ" ? true : false;
        return (
          <Chip
            label={LONGTERM_STATUS.get(row.Situacao)}
            size="small"
            style={{
              width: "100px",
              padding: "1rem 0.5rem",
              backgroundColor: status
                ? "rgba(0, 128, 0, 0.1)"
                : "rgba(255, 215, 0, 0.1)",
              border: status
                ? "1px solid rgba(0, 128, 0, 0.5)"
                : "1px solid rgba(255, 215, 0, 0.5)",
            }}
          />
        );
      },
    }),
    columnTextField({
      dataField: "DataEmissao",
      text: "Emissão",
      align: "center",
      sort: true,
      sortValue: (cell) => sortableDateValue(cell),
    }),
    columnTextField({
      dataField: "DataVencimento",
      text: "Vencimento",
      sort: true,
      sortValue: (cell) => sortableDateValue(cell),
    }),
    columnTextField({
      dataField: "DataVencimentoProrrogado",
      text: "Vencimento Prorrogado",
      sort: true,
      sortValue: (cell) => sortableDateValue(cell),
    }),
    columnTextField({
      dataField: "DtLiquidacao",
      text: "Data Pagamento",
    }),
    columnPriceField({
      dataField: "Valor",
      text: "Valor",
      align: "end",
      formatter: (value) => `R$ ${value}`,
    }),
    columnPriceField({
      dataField: "ValorEmAberto",
      text: "Valor em Aberto",
      align: "end",
      formatter: (value) => `R$ ${value}`,
    }),
    columnTextField({
      dataField: "NomeRepresentante",
      text: "Representante",
      align: "center",
      headerAlign: "center",
    }),
    columnTextField({
      dataField: "EmailCliente",
      text: "Email Cliente",
      align: "center",
      headerAlign: "center",
    }),
    columnTextField({
      dataField: "NomeTransportadora",
      text: "Transportadora",
      align: "center",
      headerAlign: "center",
    }),
    columnTextField({
      dataField: "Marca",
      text: "Marca",
    }),
    columnTextField({
      dataField: "CodigoErp",
      text: "Código ERP",
    }),
    columnTextField({
      dataField: "RazaoSocial",
      text: "Razão Social",
    }),
    columnCNPJCPFField({
      dataField: "Cnpj",
      text: "CNPJ",
    }),
    columnTextField({
      dataField: "Loja",
      text: "Loja",
    }),
    columnTextField({
      dataField: "SituacaoDias",
      text: "Situação (Dias)",
      formatter: (_, row) => checkSituationDays(row),
    }),
    columnTextField({
      dataField: "ValorTotalNf",
      text: "Valor Total NF",
      formatter: (cell) => {
        return cell !== null ? `R$ ${cell}` : "R$ 0,00";
      },
    }),
    columnNumberField({
      dataField: "NumeroNf",
      text: "Nr. NF",
    }),
    columnTextField({
      dataField: "Observacao",
      text: "Observação",
      headerAlign: "center",
      align: "center",
    }),
    columnNumberField({
      dataField: "NumeroTituloBanco",
      text: "Nr. Título Banco",
    }),
    columnTextField({
      dataField: "LinhaDigitavelBanco",
      text: "Linha Digitável Banco",
      headerAlign: "center",
      align: "center",
      formatter: (_, row) => {
        return (
          <IconButton
            size="small"
            onClick={() => UtilsFncs.CopyToClipboard(row.LinhaDigitavelBanco)}
          >
            <FileCopy fontSize="small" color="textPrimary" />
          </IconButton>
        );
      },
    }),
    columnTextField({
      dataField: "NumeroPedido",
      text: "Nr. Pedido",
      formatter: (value) => (value !== 0 && value !== "0" ? value : ""),
    }),
    columnTextField({
      dataField: "NumeroProposta",
      text: "Nr. Proposta",
      formatter: (value) => value?.replace(/["]/g, ""),
    }),
    columnTextField({
      dataField: "CondicaoPgto",
      text: "Condição Pagamento",
    }),
    columnTextField({
      dataField: "CodPortador",
      text: "Código Portador",
    }),
  ];

  const syncPaginationFromTable = (page, sizePerPage) => {
    setPaginationState((prev) => ({
      page,
      sizePerPage:
        sizePerPage != null && !Number.isNaN(Number(sizePerPage))
          ? Number(sizePerPage)
          : prev.sizePerPage,
    }));
  };

  return (
    <ToolkitTable
      dataTable={dataTable}
      keyField="__selectKey"
      columnModel={columnModel}
      showPrint
      extraActions={
        <>
          {onExportBoletos && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const selectedRows = dataTable?.filter((item) => item.selected);
                if (onExportBoletos && selectedRows?.length > 0) {
                  onExportBoletos(selectedRows);
                }
              }}
              disabled={!dataTable?.some((item) => item.selected)}
            >
              Exportar boletos
            </Button>
          )}
          {extraActions}
        </>
      }
      pagination={{
        options: [10, 25, 50, 100],
        defaultOption: paginationState.sizePerPage,
        totalSize: totalRegistros ?? dataTable?.length ?? 0,
        page: paginationState.page,
        sizePerPage: paginationState.sizePerPage,
        showTotal: true,
        onPageChange: (newPage, newSizePerPage) => {
          syncPaginationFromTable(newPage, newSizePerPage);
        },
        onSizePerPageChange: (newSizePerPage, newPage) => {
          syncPaginationFromTable(
            newPage,
            newSizePerPage != null ? newSizePerPage : undefined,
          );
        },
      }}
      notFoundTitle="Sua busca não encontrou nenhum resultado correspondente."
      style={{
        boxShadow: "none",
        margin: "0 -1rem",
      }}
    />
  );
}
